import type { FilterItineraryOption } from '@ops/entities/operation-item';
import type { ItineraryReferenceMatch } from '@travel-planner/shared/schemas';
import { useEffect, useMemo } from 'react';
import { trpc } from '@/shared/api/trpc';
import { useItineraryLabelStore } from './use-itinerary-label-store';

// An itinerary id needs resolving when the shared label cache has no
// human-readable ref/title for it — i.e. it was deep-linked (planner ->
// operations `?itineraryRef=<id>`) or arrived via a shared URL / refresh, so
// only the bare UUID is known. Deduped + sorted so the derived query key is
// stable (unchanged id set => same cache key => no refetch).
export const unresolvedItineraryIds = (
  ids: readonly string[],
  cache: ReadonlyMap<string, FilterItineraryOption>
): string[] => {
  const out = new Set<string>();
  for (const id of ids) {
    if (!id) continue;
    const cached = cache.get(id);
    if (!cached || (!cached.ref && !cached.title)) out.add(id);
  }
  return [...out].sort();
};

const toOption = (match: ItineraryReferenceMatch): FilterItineraryOption => ({
  id: match.id,
  title: match.title,
  ref: match.itineraryRef,
  country: null,
});

// Resolve any selected itinerary ids that arrived as bare UUIDs into real
// ref/title via the existing id-specific `itineraries.referencesByIds`
// endpoint, warming the shared label cache so the toolbar chip/options render a
// human label instead of the UUID. id-specific — never loads the whole
// itinerary list (only the unresolved ids). A deleted/inaccessible id simply
// returns no match and stays on the neutral fallback label — never the UUID.
export const useResolveItineraryLabels = (ids: readonly string[]): void => {
  const cache = useItineraryLabelStore((s) => s.byId);
  const cacheMany = useItineraryLabelStore((s) => s.cacheMany);

  const missing = useMemo(
    () => unresolvedItineraryIds(ids, cache),
    [ids, cache]
  );

  const { data } = trpc.itineraries.referencesByIds.useQuery(
    { ids: missing },
    { enabled: missing.length > 0, staleTime: 5 * 60 * 1000 }
  );

  useEffect(() => {
    if (!data || data.length === 0) return;
    cacheMany(data.map(toOption));
  }, [data, cacheMany]);
};
